/**
 * FikoVIO - Visual Inertial Odometry System
 *
 * Profesyonel 6-DoF pose estimation:
 * - Madgwick AHRS algoritması ile stabil orientation
 * - IMU preintegration ile pozisyon tahmini
 * - Visual tracking (marker) ile drift düzeltme
 * - ZUPT (Zero Velocity Update) ile hareketsiz anlar
 *
 * Referanslar:
 * - Madgwick AHRS: https://x-io.co.uk/open-source-imu-and-ahrs-algorithms/
 * - VIO: ARCore/ARKit yaklaşımı
 * - IMU Preintegration: GTSAM methodology
 */

import * as THREE from 'three';

/** IMU ham verileri */
export interface IMURawData {
    // Gyroscope (rad/s)
    gyroX: number;
    gyroY: number;
    gyroZ: number;

    // Accelerometer (m/s²)
    accelX: number;
    accelY: number;
    accelZ: number;

    // Magnetometer/Compass (optional)
    magX?: number;
    magY?: number;
    magZ?: number;

    // DeviceOrientation (degrees)
    alpha: number;  // Z-axis (0-360)
    beta: number;   // X-axis (-180 to 180)
    gamma: number;  // Y-axis (-90 to 90)

    timestamp: number;
}

/** VIO Pose çıktısı */
export interface VIOPose {
    // Pozisyon (mm)
    position: THREE.Vector3;

    // Orientation (quaternion - gimbal lock yok)
    orientation: THREE.Quaternion;

    // Velocity (mm/s)
    velocity: THREE.Vector3;

    // Güven skoru (0-1)
    confidence: number;

    // Drift miktarı
    estimatedDrift: number;

    timestamp: number;
}

/** Visual Observation (marker tracking'den) */
export interface VisualObservation {
    // Marker'dan gelen poz
    position: THREE.Vector3;
    rotation: THREE.Euler;
    rotationMatrix: THREE.Matrix4;

    // Güven (marker görünürlüğüne göre)
    confidence: number;

    timestamp: number;
}

/**
 * Madgwick AHRS Filter
 *
 * Sebastian Madgwick'in gradient descent tabanlı orientation filter'ı.
 * Gyroscope, accelerometer ve opsiyonel magnetometer kullanır.
 *
 * Avantajları:
 * - Düşük hesaplama maliyeti
 * - Gyro drift'ini otomatik düzeltir
 * - Gimbal lock problemi yok (quaternion tabanlı)
 */
class MadgwickAHRS {
    private samplePeriod: number;
    private beta: number; // Algorithm gain (0.1 önerilen)
    private q: THREE.Quaternion;

    constructor(samplePeriod: number = 1 / 30, beta: number = 0.1) {
        this.samplePeriod = samplePeriod;
        this.beta = beta;
        this.q = new THREE.Quaternion(0, 0, 0, 1);
    }

    /**
     * IMU güncellemesi (gyro + accel)
     */
    updateIMU(gx: number, gy: number, gz: number, ax: number, ay: number, az: number): void {
        let q0 = this.q.w, q1 = this.q.x, q2 = this.q.y, q3 = this.q.z;

        // Normalise accelerometer measurement
        let recipNorm = 1.0 / Math.sqrt(ax * ax + ay * ay + az * az);
        if (!isFinite(recipNorm)) return;

        ax *= recipNorm;
        ay *= recipNorm;
        az *= recipNorm;

        // Auxiliary variables to avoid repeated arithmetic
        const _2q0 = 2.0 * q0;
        const _2q1 = 2.0 * q1;
        const _2q2 = 2.0 * q2;
        const _2q3 = 2.0 * q3;
        const _4q0 = 4.0 * q0;
        const _4q1 = 4.0 * q1;
        const _4q2 = 4.0 * q2;
        const _8q1 = 8.0 * q1;
        const _8q2 = 8.0 * q2;
        const q0q0 = q0 * q0;
        const q1q1 = q1 * q1;
        const q2q2 = q2 * q2;
        const q3q3 = q3 * q3;

        // Gradient descent algorithm corrective step
        let s0 = _4q0 * q2q2 + _2q2 * ax + _4q0 * q1q1 - _2q1 * ay;
        let s1 = _4q1 * q3q3 - _2q3 * ax + 4.0 * q0q0 * q1 - _2q0 * ay - _4q1 + _8q1 * q1q1 + _8q1 * q2q2 + _4q1 * az;
        let s2 = 4.0 * q0q0 * q2 + _2q0 * ax + _4q2 * q3q3 - _2q3 * ay - _4q2 + _8q2 * q1q1 + _8q2 * q2q2 + _4q2 * az;
        let s3 = 4.0 * q1q1 * q3 - _2q1 * ax + 4.0 * q2q2 * q3 - _2q2 * ay;

        // Normalise step magnitude
        recipNorm = 1.0 / Math.sqrt(s0 * s0 + s1 * s1 + s2 * s2 + s3 * s3);
        s0 *= recipNorm;
        s1 *= recipNorm;
        s2 *= recipNorm;
        s3 *= recipNorm;

        // Apply feedback step
        const qDot0 = 0.5 * (-q1 * gx - q2 * gy - q3 * gz) - this.beta * s0;
        const qDot1 = 0.5 * (q0 * gx + q2 * gz - q3 * gy) - this.beta * s1;
        const qDot2 = 0.5 * (q0 * gy - q1 * gz + q3 * gx) - this.beta * s2;
        const qDot3 = 0.5 * (q0 * gz + q1 * gy - q2 * gx) - this.beta * s3;

        // Integrate to yield quaternion
        q0 += qDot0 * this.samplePeriod;
        q1 += qDot1 * this.samplePeriod;
        q2 += qDot2 * this.samplePeriod;
        q3 += qDot3 * this.samplePeriod;

        // Normalise quaternion
        recipNorm = 1.0 / Math.sqrt(q0 * q0 + q1 * q1 + q2 * q2 + q3 * q3);
        this.q.set(q1 * recipNorm, q2 * recipNorm, q3 * recipNorm, q0 * recipNorm);
    }

    /**
     * AHRS güncellemesi (gyro + accel + mag)
     */
    updateAHRS(gx: number, gy: number, gz: number, ax: number, ay: number, az: number, mx: number, my: number, mz: number): void {
        let q0 = this.q.w, q1 = this.q.x, q2 = this.q.y, q3 = this.q.z;

        // Use IMU algorithm if magnetometer measurement invalid
        if (mx === 0 && my === 0 && mz === 0) {
            this.updateIMU(gx, gy, gz, ax, ay, az);
            return;
        }

        // Normalise accelerometer measurement
        let recipNorm = 1.0 / Math.sqrt(ax * ax + ay * ay + az * az);
        if (!isFinite(recipNorm)) return;
        ax *= recipNorm;
        ay *= recipNorm;
        az *= recipNorm;

        // Normalise magnetometer measurement
        recipNorm = 1.0 / Math.sqrt(mx * mx + my * my + mz * mz);
        if (!isFinite(recipNorm)) return;
        mx *= recipNorm;
        my *= recipNorm;
        mz *= recipNorm;

        // Auxiliary variables
        const _2q0mx = 2.0 * q0 * mx;
        const _2q0my = 2.0 * q0 * my;
        const _2q0mz = 2.0 * q0 * mz;
        const _2q1mx = 2.0 * q1 * mx;
        const _2q0 = 2.0 * q0;
        const _2q1 = 2.0 * q1;
        const _2q2 = 2.0 * q2;
        const _2q3 = 2.0 * q3;
        const _2q0q2 = 2.0 * q0 * q2;
        const _2q2q3 = 2.0 * q2 * q3;
        const q0q0 = q0 * q0;
        const q0q1 = q0 * q1;
        const q0q2 = q0 * q2;
        const q0q3 = q0 * q3;
        const q1q1 = q1 * q1;
        const q1q2 = q1 * q2;
        const q1q3 = q1 * q3;
        const q2q2 = q2 * q2;
        const q2q3 = q2 * q3;
        const q3q3 = q3 * q3;

        // Reference direction of Earth's magnetic field
        const hx = mx * q0q0 - _2q0my * q3 + _2q0mz * q2 + mx * q1q1 + _2q1 * my * q2 + _2q1 * mz * q3 - mx * q2q2 - mx * q3q3;
        const hy = _2q0mx * q3 + my * q0q0 - _2q0mz * q1 + _2q1mx * q2 - my * q1q1 + my * q2q2 + _2q2 * mz * q3 - my * q3q3;
        const _2bx = Math.sqrt(hx * hx + hy * hy);
        const _2bz = -_2q0mx * q2 + _2q0my * q1 + mz * q0q0 + _2q1mx * q3 - mz * q1q1 + _2q2 * my * q3 - mz * q2q2 + mz * q3q3;
        const _4bx = 2.0 * _2bx;
        const _4bz = 2.0 * _2bz;

        // Gradient descent algorithm corrective step
        let s0 = -_2q2 * (2.0 * q1q3 - _2q0q2 - ax) + _2q1 * (2.0 * q0q1 + _2q2q3 - ay) - _2bz * q2 * (_2bx * (0.5 - q2q2 - q3q3) + _2bz * (q1q3 - q0q2) - mx) + (-_2bx * q3 + _2bz * q1) * (_2bx * (q1q2 - q0q3) + _2bz * (q0q1 + q2q3) - my) + _2bx * q2 * (_2bx * (q0q2 + q1q3) + _2bz * (0.5 - q1q1 - q2q2) - mz);
        let s1 = _2q3 * (2.0 * q1q3 - _2q0q2 - ax) + _2q0 * (2.0 * q0q1 + _2q2q3 - ay) - 4.0 * q1 * (1 - 2.0 * q1q1 - 2.0 * q2q2 - az) + _2bz * q3 * (_2bx * (0.5 - q2q2 - q3q3) + _2bz * (q1q3 - q0q2) - mx) + (_2bx * q2 + _2bz * q0) * (_2bx * (q1q2 - q0q3) + _2bz * (q0q1 + q2q3) - my) + (_2bx * q3 - _4bz * q1) * (_2bx * (q0q2 + q1q3) + _2bz * (0.5 - q1q1 - q2q2) - mz);
        let s2 = -_2q0 * (2.0 * q1q3 - _2q0q2 - ax) + _2q3 * (2.0 * q0q1 + _2q2q3 - ay) - 4.0 * q2 * (1 - 2.0 * q1q1 - 2.0 * q2q2 - az) + (-_4bx * q2 - _2bz * q0) * (_2bx * (0.5 - q2q2 - q3q3) + _2bz * (q1q3 - q0q2) - mx) + (_2bx * q1 + _2bz * q3) * (_2bx * (q1q2 - q0q3) + _2bz * (q0q1 + q2q3) - my) + (_2bx * q0 - _4bz * q2) * (_2bx * (q0q2 + q1q3) + _2bz * (0.5 - q1q1 - q2q2) - mz);
        let s3 = _2q1 * (2.0 * q1q3 - _2q0q2 - ax) + _2q2 * (2.0 * q0q1 + _2q2q3 - ay) + (-_4bx * q3 + _2bz * q1) * (_2bx * (0.5 - q2q2 - q3q3) + _2bz * (q1q3 - q0q2) - mx) + (-_2bx * q0 + _2bz * q2) * (_2bx * (q1q2 - q0q3) + _2bz * (q0q1 + q2q3) - my) + _2bx * q1 * (_2bx * (q0q2 + q1q3) + _2bz * (0.5 - q1q1 - q2q2) - mz);

        // Normalise step magnitude
        recipNorm = 1.0 / Math.sqrt(s0 * s0 + s1 * s1 + s2 * s2 + s3 * s3);
        s0 *= recipNorm;
        s1 *= recipNorm;
        s2 *= recipNorm;
        s3 *= recipNorm;

        // Apply feedback step
        const qDot0 = 0.5 * (-q1 * gx - q2 * gy - q3 * gz) - this.beta * s0;
        const qDot1 = 0.5 * (q0 * gx + q2 * gz - q3 * gy) - this.beta * s1;
        const qDot2 = 0.5 * (q0 * gy - q1 * gz + q3 * gx) - this.beta * s2;
        const qDot3 = 0.5 * (q0 * gz + q1 * gy - q2 * gx) - this.beta * s3;

        // Integrate to yield quaternion
        q0 += qDot0 * this.samplePeriod;
        q1 += qDot1 * this.samplePeriod;
        q2 += qDot2 * this.samplePeriod;
        q3 += qDot3 * this.samplePeriod;

        // Normalise quaternion
        recipNorm = 1.0 / Math.sqrt(q0 * q0 + q1 * q1 + q2 * q2 + q3 * q3);
        this.q.set(q1 * recipNorm, q2 * recipNorm, q3 * recipNorm, q0 * recipNorm);
    }

    getQuaternion(): THREE.Quaternion {
        return this.q.clone();
    }

    reset(): void {
        this.q.set(0, 0, 0, 1);
    }

    setBeta(beta: number): void {
        this.beta = beta;
    }
}

/**
 * IMU Preintegration
 *
 * Accelerometer ve gyroscope verilerini entegre ederek
 * pozisyon ve velocity tahmin eder.
 *
 * Drift düzeltme yöntemleri:
 * 1. ZUPT - Zero Velocity Update (hareketsiz anlar)
 * 2. Visual correction (marker görüldüğünde)
 */
class IMUPreintegration {
    // Durum değişkenleri
    private position: THREE.Vector3 = new THREE.Vector3();
    private velocity: THREE.Vector3 = new THREE.Vector3();
    private orientation: THREE.Quaternion = new THREE.Quaternion();

    // Bias değerleri (kalibrasyonla güncellenir)
    private accelBias: THREE.Vector3 = new THREE.Vector3();
    private gyroBias: THREE.Vector3 = new THREE.Vector3();

    // Gravity vektörü (m/s² - dünya koordinatlarında)
    private readonly gravity: THREE.Vector3 = new THREE.Vector3(0, 0, -9.81);

    // Son güncelleme zamanı
    private lastTimestamp: number = 0;

    // Hareket algılama
    private accelHistory: number[] = [];
    private readonly historySize: number = 10;
    private readonly stationaryThreshold: number = 0.3; // m/s² - düşük = hassas

    // Drift tahmin
    private driftEstimate: number = 0;
    private integrationTime: number = 0;

    constructor() {}

    /**
     * IMU verilerini işle ve pose güncelle
     */
    update(
        accel: THREE.Vector3, // m/s² (device frame)
        gyro: THREE.Vector3,  // rad/s
        orientation: THREE.Quaternion, // AHRS'den
        timestamp: number
    ): { position: THREE.Vector3; velocity: THREE.Vector3; isStationary: boolean } {
        // Delta time hesapla
        const dt = this.lastTimestamp > 0 ? (timestamp - this.lastTimestamp) / 1000 : 0;
        this.lastTimestamp = timestamp;

        if (dt <= 0 || dt > 0.5) {
            // Geçersiz dt, sadece orientation güncelle
            this.orientation.copy(orientation);
            return {
                position: this.position.clone(),
                velocity: this.velocity.clone(),
                isStationary: true
            };
        }

        // Orientation güncelle
        this.orientation.copy(orientation);

        // Bias düzelt
        const correctedAccel = accel.clone().sub(this.accelBias);

        // Device frame -> World frame dönüşümü
        const worldAccel = correctedAccel.clone();
        worldAccel.applyQuaternion(orientation);

        // Gravity çıkar
        worldAccel.sub(this.gravity);

        // Hareket algılama (ZUPT için)
        const accelMagnitude = correctedAccel.length();
        this.accelHistory.push(accelMagnitude);
        if (this.accelHistory.length > this.historySize) {
            this.accelHistory.shift();
        }

        const isStationary = this.detectStationary();

        if (isStationary) {
            // ZUPT - Zero Velocity Update
            // Hareketsiz iken velocity sıfırla (drift düzeltme)
            this.velocity.multiplyScalar(0.9); // Yumuşak sıfırlama

            // Accel bias güncelle (hareketsiz iken gravity'den sapma = bias)
            const expectedAccel = 9.81; // m/s²
            const measuredAccel = correctedAccel.length();
            const biasMagnitude = Math.abs(measuredAccel - expectedAccel);
            if (biasMagnitude < 1.0) {
                // Bias kalibre et
                const expectedGravity = new THREE.Vector3(0, 0, -9.81);
                const measuredGravity = correctedAccel.clone().multiplyScalar(9.81 / measuredAccel);
                this.accelBias.lerp(correctedAccel.clone().sub(measuredGravity.applyQuaternion(orientation.clone().invert())), 0.01);
            }
        } else {
            // Hareket var - normal integrasyon

            // Velocity güncelle (single integration)
            // v = v + a * dt
            this.velocity.add(worldAccel.clone().multiplyScalar(dt * 1000)); // mm/s'ye çevir

            // Position güncelle (double integration)
            // p = p + v * dt + 0.5 * a * dt²
            this.position.add(this.velocity.clone().multiplyScalar(dt));
            this.position.add(worldAccel.clone().multiplyScalar(0.5 * dt * dt * 1000));

            // Drift tahmin (t² ile büyür)
            this.integrationTime += dt;
            this.driftEstimate = 0.001 * this.integrationTime * this.integrationTime; // Yaklaşık drift (mm)
        }

        return {
            position: this.position.clone(),
            velocity: this.velocity.clone(),
            isStationary
        };
    }

    /**
     * Hareketsiz durumu algıla
     */
    private detectStationary(): boolean {
        if (this.accelHistory.length < 3) return true;

        // Varyans hesapla
        const mean = this.accelHistory.reduce((a, b) => a + b, 0) / this.accelHistory.length;
        const variance = this.accelHistory.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / this.accelHistory.length;
        const stdDev = Math.sqrt(variance);

        // Düşük varyans = hareketsiz
        return stdDev < this.stationaryThreshold && Math.abs(mean - 9.81) < 1.0;
    }

    /**
     * Visual observation ile düzelt (marker görüldüğünde)
     */
    correctWithVisual(visualPosition: THREE.Vector3, confidence: number): void {
        // Weighted correction
        const alpha = confidence * 0.5; // Max %50 düzeltme

        // Position düzelt
        this.position.lerp(visualPosition, alpha);

        // Drift sıfırla
        this.integrationTime = 0;
        this.driftEstimate = 0;
    }

    /**
     * Orientation ile düzelt
     */
    correctWithVisualOrientation(visualOrientation: THREE.Quaternion, confidence: number): void {
        const alpha = confidence * 0.3;
        this.orientation.slerp(visualOrientation, alpha);
    }

    getPosition(): THREE.Vector3 {
        return this.position.clone();
    }

    getVelocity(): THREE.Vector3 {
        return this.velocity.clone();
    }

    getOrientation(): THREE.Quaternion {
        return this.orientation.clone();
    }

    getDriftEstimate(): number {
        return this.driftEstimate;
    }

    reset(): void {
        this.position.set(0, 0, 0);
        this.velocity.set(0, 0, 0);
        this.orientation.set(0, 0, 0, 1);
        this.lastTimestamp = 0;
        this.integrationTime = 0;
        this.driftEstimate = 0;
        this.accelHistory = [];
    }

    setPosition(pos: THREE.Vector3): void {
        this.position.copy(pos);
    }
}

/**
 * FikoVIO - Ana VIO Sınıfı
 *
 * Madgwick AHRS + IMU Preintegration + Visual Correction
 */
export class FikoVIO {
    private ahrs: MadgwickAHRS;
    private preintegration: IMUPreintegration;

    // Son visual observation
    private lastVisualObs: VisualObservation | null = null;
    private visualObsCount: number = 0;

    // Kalibrasyon
    private isCalibrated: boolean = false;
    private calibrationSamples: IMURawData[] = [];
    private readonly calibrationCount: number = 30;

    // Reference frame (ilk marker görüldüğünde set edilir)
    private referenceSet: boolean = false;
    private referenceOrientation: THREE.Quaternion = new THREE.Quaternion();
    private referencePosition: THREE.Vector3 = new THREE.Vector3();

    // Yapılandırma
    private config = {
        ahrsBeta: 0.1,           // Madgwick filter gain
        visualWeight: 0.7,       // Visual vs IMU weight (0=IMU, 1=Visual)
        useVisualPosition: true, // Visual position correction
        useVisualOrientation: true, // Visual orientation correction
        maxDriftBeforeReset: 100 // mm - max drift before reset
    };

    constructor() {
        this.ahrs = new MadgwickAHRS(1 / 30, this.config.ahrsBeta);
        this.preintegration = new IMUPreintegration();
    }

    /**
     * IMU verisi işle
     */
    processIMU(data: IMURawData): VIOPose | null {
        // Kalibrasyon aşamasında sample topla
        if (!this.isCalibrated) {
            this.calibrationSamples.push(data);
            if (this.calibrationSamples.length >= this.calibrationCount) {
                this.calibrate();
            }
            return null;
        }

        // DeviceOrientation verilerinden gyro değerleri oluştur (deg/s -> rad/s)
        // NOT: Gerçek gyro yoksa rotationRate kullan
        let gx = data.gyroX || (data.alpha * Math.PI / 180);
        let gy = data.gyroY || (data.beta * Math.PI / 180);
        let gz = data.gyroZ || (data.gamma * Math.PI / 180);

        // Eğer rotationRate varsa onu kullan
        if (Math.abs(data.gyroX) > 0.001 || Math.abs(data.gyroY) > 0.001 || Math.abs(data.gyroZ) > 0.001) {
            gx = data.gyroX * Math.PI / 180; // deg/s -> rad/s
            gy = data.gyroY * Math.PI / 180;
            gz = data.gyroZ * Math.PI / 180;
        }

        // Accelerometer (m/s²)
        const ax = data.accelX;
        const ay = data.accelY;
        const az = data.accelZ;

        // AHRS güncelle (orientation)
        if (data.magX !== undefined && data.magY !== undefined && data.magZ !== undefined) {
            this.ahrs.updateAHRS(gx, gy, gz, ax, ay, az, data.magX, data.magY, data.magZ);
        } else {
            this.ahrs.updateIMU(gx, gy, gz, ax, ay, az);
        }

        const orientation = this.ahrs.getQuaternion();

        // IMU Preintegration güncelle (position + velocity)
        const accel = new THREE.Vector3(ax, ay, az);
        const gyro = new THREE.Vector3(gx, gy, gz);
        const { position, velocity, isStationary } = this.preintegration.update(
            accel, gyro, orientation, data.timestamp
        );

        // Reference frame'e göre düzelt
        let finalPosition = position;
        let finalOrientation = orientation;

        if (this.referenceSet) {
            // Reference orientation'a göre düzelt
            finalOrientation = this.referenceOrientation.clone().multiply(orientation);

            // Reference position'a göre offset ekle
            finalPosition = position.clone().add(this.referencePosition);
        }

        // Confidence hesapla
        let confidence = 1.0;
        const drift = this.preintegration.getDriftEstimate();

        // Drift arttıkça confidence düşer
        confidence *= Math.max(0.1, 1.0 - drift / this.config.maxDriftBeforeReset);

        // Visual observation varsa ve yakınsa confidence artar
        if (this.lastVisualObs && (data.timestamp - this.lastVisualObs.timestamp) < 500) {
            confidence = Math.min(1.0, confidence + 0.3 * this.lastVisualObs.confidence);
        }

        return {
            position: finalPosition,
            orientation: finalOrientation,
            velocity: velocity,
            confidence: confidence,
            estimatedDrift: drift,
            timestamp: data.timestamp
        };
    }

    /**
     * Visual observation ekle (marker tracking'den)
     */
    addVisualObservation(obs: VisualObservation): void {
        this.lastVisualObs = obs;
        this.visualObsCount++;

        // İlk observation - reference frame ayarla
        if (!this.referenceSet && obs.confidence > 0.5) {
            this.setReference(obs);
        }

        // Position düzeltme
        if (this.config.useVisualPosition) {
            this.preintegration.correctWithVisual(obs.position, obs.confidence * this.config.visualWeight);
        }

        // Orientation düzeltme
        if (this.config.useVisualOrientation) {
            const visualQuat = new THREE.Quaternion().setFromRotationMatrix(obs.rotationMatrix);
            this.preintegration.correctWithVisualOrientation(visualQuat, obs.confidence * this.config.visualWeight);
        }
    }

    /**
     * Reference frame ayarla (ilk marker)
     */
    private setReference(obs: VisualObservation): void {
        this.referencePosition.copy(obs.position);
        this.referenceOrientation.setFromRotationMatrix(obs.rotationMatrix);
        this.referenceSet = true;

        console.log('[VIO] Reference frame set:', {
            position: `(${obs.position.x.toFixed(1)}, ${obs.position.y.toFixed(1)}, ${obs.position.z.toFixed(1)})`
        });
    }

    /**
     * IMU kalibrasyonu
     */
    private calibrate(): void {
        if (this.calibrationSamples.length < this.calibrationCount) return;

        // Ortalama accel hesapla (gravity için)
        let sumAx = 0, sumAy = 0, sumAz = 0;

        for (const sample of this.calibrationSamples) {
            sumAx += sample.accelX;
            sumAy += sample.accelY;
            sumAz += sample.accelZ;
        }

        const avgAx = sumAx / this.calibrationSamples.length;
        const avgAy = sumAy / this.calibrationSamples.length;
        const avgAz = sumAz / this.calibrationSamples.length;

        const avgAccelMag = Math.sqrt(avgAx * avgAx + avgAy * avgAy + avgAz * avgAz);

        console.log('[VIO] Calibration complete:', {
            avgAccel: `(${avgAx.toFixed(2)}, ${avgAy.toFixed(2)}, ${avgAz.toFixed(2)})`,
            magnitude: avgAccelMag.toFixed(2)
        });

        this.isCalibrated = true;
        this.calibrationSamples = [];
    }

    /**
     * Mevcut pose al
     */
    getPose(): VIOPose {
        return {
            position: this.preintegration.getPosition(),
            orientation: this.preintegration.getOrientation(),
            velocity: this.preintegration.getVelocity(),
            confidence: this.isCalibrated ? 0.5 : 0,
            estimatedDrift: this.preintegration.getDriftEstimate(),
            timestamp: Date.now()
        };
    }

    /**
     * Kalibre edildi mi?
     */
    isReady(): boolean {
        return this.isCalibrated;
    }

    /**
     * Reference set edildi mi?
     */
    hasReference(): boolean {
        return this.referenceSet;
    }

    /**
     * Sıfırla
     */
    reset(): void {
        this.ahrs.reset();
        this.preintegration.reset();
        this.lastVisualObs = null;
        this.visualObsCount = 0;
        this.referenceSet = false;
        this.referenceOrientation.identity();
        this.referencePosition.set(0, 0, 0);
        // Kalibrasyon koru
    }

    /**
     * Tam sıfırla (kalibrasyon dahil)
     */
    fullReset(): void {
        this.reset();
        this.isCalibrated = false;
        this.calibrationSamples = [];
    }

    /**
     * Yapılandırma güncelle
     */
    setConfig(config: Partial<typeof this.config>): void {
        Object.assign(this.config, config);
        if (config.ahrsBeta !== undefined) {
            this.ahrs.setBeta(config.ahrsBeta);
        }
    }

    /**
     * Debug bilgisi
     */
    getDebugInfo(): {
        isCalibrated: boolean;
        referenceSet: boolean;
        visualObsCount: number;
        drift: number;
        velocity: THREE.Vector3;
    } {
        return {
            isCalibrated: this.isCalibrated,
            referenceSet: this.referenceSet,
            visualObsCount: this.visualObsCount,
            drift: this.preintegration.getDriftEstimate(),
            velocity: this.preintegration.getVelocity()
        };
    }
}
